import React, {useState} from "react";
import {useForm} from "react-hook-form";
import {useLocation, useNavigate} from "react-router-dom";
import FormHeader from "../../Common/FormHeader";
import ColorPalette from "../ColorPalette";
import IOSSwitch from "../../Common/IOSSwitch";
import secureStorage from "../../../utils/secureStorage";
import {api} from "../../../config/api";
import Notification from "../../Common/Notification";

const NewRemiseForm = () => {
  const navigate = useNavigate();
  // Back to the catalogue tab we came from (set by its "Nouveau" button)
  const returnTo = useLocation().state?.returnTo || "/catalogue";
  const [currentColor, setCurrentColor] = useState("#FFFF00");
  const [showPalette, setShowPalette] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [discountType, setDiscountType] = useState("%"); // % or DH
  const [discountImage, setDiscountImage] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    type: 'success',
    message: '',
  });

  const {
    register,
    handleSubmit,
    formState: {errors},
  } = useForm({
    defaultValues: {
      name: "",
      value: "",
      description: ""
    }
  });

  // Function to show a notification
  const showNotification = (type, message) => {
    setNotification({
      show: true,
      type,
      message,
    });

    // Auto hide after 5 seconds for success notifications
    if (type === 'success') {
      setTimeout(() => {
        setNotification(prev => ({...prev, show: false}));
      }, 5000);
    }
  };

  // Function to hide notification
  const hideNotification = () => {
    setNotification(prev => ({...prev, show: false}));
  };

  const onSubmit = async (data) => {
    try {
      showNotification('loading', "Création de la remise en cours...");

      // Format value to ensure it's a number
      const valueAsNumber = parseFloat(data.value);

      if (isNaN(valueAsNumber)) {
        throw new Error("La valeur doit être un nombre valide");
      }

      // Prepare payload
      const payload = {
        name: data.name,
        value: valueAsNumber,
        type: discountType,
        active: isActive,
        color: currentColor,
        description: data.description || ""
      };

      // Get account ID
      const accountId = await secureStorage.getItem("selectedAccount");

      // Call API
      const response = await api.post('/discounts', payload, {
        headers: {
          'X-account-id': accountId.id,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log("Discount created:", response.data);

      // If an image was selected, upload it via the dedicated multipart
      // /update route (POST /discounts is JSON-only and can't carry a file).
      if (discountImage && response.data?.id) {
        const formData = new FormData();

        if (discountImage instanceof File) {
          formData.append("image", discountImage);
        } else if (typeof discountImage === 'string' && discountImage.startsWith('data:')) {
          const imgResponse = await fetch(discountImage);
          const blob = await imgResponse.blob();
          const file = new File([blob], "discount-image.jpg", {type: "image/jpeg"});
          formData.append("image", file);
        }

        if (formData.has("image")) {
          await api.post(`/discounts/${response.data.id}/update`, formData, {
            headers: {
              'X-account-id': accountId.id,
              'Accept': 'application/json',
              'Content-Type': 'multipart/form-data'
            }
          });
        }
      }

      showNotification('success', "Remise créée avec succès");

      // Give time for notification to be seen
      setTimeout(() => {
        navigate(returnTo);
      }, 1500);
    } catch (error) {
      console.error("Error creating discount:", error);
      const errorMessage = error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          "Une erreur s'est produite";
      showNotification('error', `Erreur: ${errorMessage}`);
    }
  };

  const handleColorChange = (color) => {
    setCurrentColor(color);
    setShowPalette(false);
  };

  const togglePalette = () => {
    setShowPalette((prev) => !prev);
  };

  const toggleActive = () => {
    setIsActive((prev) => !prev);
  };

  // Helper function to determine text color based on background color
  const getContrastColor = (hexColor) => {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);

    // Calculate luminance (perceived brightness)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return black for bright colors, white for dark colors
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        {/* Header */}
        <FormHeader title="Nouvelle remise" back={returnTo} onSave={handleSubmit(onSubmit)}/>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-6 space-y-6">
          {/* Color picker button */}
          <div className="flex flex-col items-center mb-6">
            <div
                onClick={togglePalette}
                style={{backgroundColor: currentColor, color: getContrastColor(currentColor)}}
                className="w-24 h-24 rounded-lg cursor-pointer flex items-center justify-center shadow-md"
            >
              <span className="text-sm font-medium text-white">Éditer</span>
            </div>
            <p className="text-gray-500 text-xs mt-2">Cliquez pour modifier</p>
          </div>

          {/* Form fields */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="name" className="text-gray-700 dark:text-gray-300 font-medium block mb-1 text-sm">
                Nom :
              </label>
              <input
                  type="text"
                  id="name"
                  {...register("name", {
                    required: "Le champ 'Nom' est obligatoire.",
                  })}
                  placeholder="Nom de la remise*"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg"
              />
              {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label htmlFor="value" className="text-gray-700 dark:text-gray-300 font-medium block mb-1 text-sm">
                  Valeur :
                </label>
                <div className="relative">
                  <input
                      type="number"
                      id="value"
                      step="0.01"
                      {...register("value", {
                        required: "Le champ 'Valeur' est obligatoire.",
                        min: {
                          value: 0,
                          message: "La valeur doit être positive."
                        },
                      })}
                      placeholder="Valeur*"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-500 text-sm">{discountType}</span>
                  </div>
                </div>
                {errors.value && (
                    <p className="text-red-500 text-xs mt-1">{errors.value.message}</p>
                )}
              </div>

              <div>
                <label className="text-gray-700 dark:text-gray-300 font-medium block mb-1 text-sm">
                  Type de remise :
                </label>
                <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                  <button
                      type="button"
                      onClick={() => setDiscountType("DH")}
                      className={`px-6 py-2 flex items-center justify-center text-sm ${
                          discountType === "DH"
                              ? "bg-primary text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    <span>DH</span>
                  </button>
                  <button
                      type="button"
                      onClick={() => setDiscountType("%")}
                      className={`px-6 py-2 flex items-center justify-center text-sm ${
                          discountType === "%"
                              ? "bg-primary text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    <span>%</span>
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="description"
                     className="text-gray-700 dark:text-gray-300 font-medium block mb-1 text-sm">
                Description (optionnelle) :
              </label>
              <textarea
                  id="description"
                  {...register("description")}
                  rows="3"
                  placeholder="Description de la remise"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg"
              />
            </div>

            <div className="flex items-center">
              <span className="text-gray-700 dark:text-gray-300 font-medium mr-4 text-sm">Actif :</span>
              <IOSSwitch checked={isActive} onChange={toggleActive}/>
            </div>
          </form>
        </div>

        {/* Color palette overlay */}
        {showPalette && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-xl mx-4">
                <ColorPalette
                    initialColor={currentColor}
                    onClose={togglePalette}
                    onColorChange={handleColorChange}
                    onImageChange={setDiscountImage}
                />
              </div>
            </div>
        )}

        {/* Notification */}
        <Notification
            show={notification.show}
            type={notification.type}
            message={notification.message}
            onClose={hideNotification}
        />
      </div>
  );
};

export default NewRemiseForm;
